import React, { useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select, SelectProps, Space, Table, Typography } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import PageWithFilters from '../page/page-with-filters';
import { PageWithFiltersContentContainer } from '../page/page.styles';
import withAuthorization from '../hocs/withAuthorization';
import { Countries, RealmRoles, USAGE_LIMITS_STATES } from '../../constants';
import useGetRequestUpgrade from '../../hooks/utils/useGetRequestUpgrade';
import useToastContext from '../../hooks/useToastContext';
import usePostRequestUpgrade from '../../hooks/utils/usePostRequestUpgrade';
import { validateStatus } from '../../utils/utils';
import { UsageLimitState } from './UsageLimitState';
import { UsageLimits } from './usage-limits';

const NewUsageLimitForm = ({ onCancel, execute }) => {
  const toast = useToastContext();
  const { postLoading, postError, executePost } = usePostRequestUpgrade({ path: `v1/usage_limits` });
  const [form] = Form.useForm();
  const options: SelectProps['options'] = [];

  const onSubmit = async (data: UsageLimits) => {
    try {
      const response = await executePost({ data: data })
      if(validateStatus(response.status)) {
        toast.success('Exito', 'Limitador de uso creado satisfactoriamente!.');
        form.resetFields();
        execute();
        onCancel();
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error: ${postError}, porfavor, intenta nuevamente.`);
    }
  }

  return (
    <>
      <Typography.Title level={3}>Crear nuevo Limitador de uso</Typography.Title>
      <Form<UsageLimits>
        layout="vertical"
        onFinish={onSubmit}
        form={form}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item<UsageLimits>
              label="Nombre"
              name="name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Compras en E-commerce"
              name="allow_ecommerce"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Compras con chip"
              name="allow_purchase_chip"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Compras con contactless"
              name="allow_purchase_contactless"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Consulta de Balance/Operaciones en ATM"
              name="allow_atm_operation"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Extracciones en ATM"
              name="allow_atm_withdrawal"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
          </Col>
          <Col  span={12}>
            <Form.Item<UsageLimits>
              label="Códigos MCC prohibios"
              name="banned_mcc_list"
              rules={[{ required: false }]}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                tokenSeparators={[',']}
                options={options}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Códigos TCC Prohibidos"
              name="banned_tcc_list"
              rules={[{ required: false }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                tokenSeparators={[',']}
                options={options}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Paises prohibidos"
              name="countries_black_list"
              rules={[{ required: false }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                mode="multiple"
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(Countries).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Paises habilitados"
              name="countries_white_list"
              rules={[{ required: false }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                mode="multiple"
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(Countries).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Operaciones OCT"
              name="allow_oct"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
            <Form.Item<UsageLimits>
              label="Compras con banda magnetica"
              name="allow_purchase_magnetic_stripe"
              rules={[{ required: true }]}
              style={{ marginTop: '3rem' }}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
          </Col>
          <Col span={24} style={{ textAlign: 'center', margin: '2rem' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={postLoading}
            >
              Crear
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}

const UsageLimitsPage = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const [usageLimitModalOpen, setUsageLimitModalOpen] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const { getData: usageLimits, getLoading: isLoading, executeGet } = useGetRequestUpgrade({ path : `v1/usage_limits?page_size=${paginationModel.pageSize}&page_number=${paginationModel.page}` });

  const NewUsageLimitBtn = withAuthorization([RealmRoles.UsageLimitsWrite, RealmRoles.CardHolderAllWrite], () => (
    <Button icon={<PlusOutlined />} onClick={() => setUsageLimitModalOpen(true) } type="primary">
      Nuevo
    </Button>
  ));

  const handleViewUsageLimitDetailClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, usageLimitId: string) => {
    e.preventDefault();
    navigate(`/${issuer}/usage-limits/${usageLimitId}`);
  };
  
  const columns: ColumnsType<UsageLimits> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Nombre',
      render: (_, record) => {
        return record?.name;
      },
    },
    {
      dataIndex: 'created_timestamp',
      key: 'created_timestamp',
      title: 'Fecha de creación',
    },
    {
      dataIndex: 'allow_ecommerce',
      key: 'allow_ecommerce',
      title: 'Comercios electrónicos',
      render: (_, record) => {
        return record?.allow_ecommerce ? USAGE_LIMITS_STATES[record?.allow_ecommerce] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_purchase_chip',
      key: 'allow_purchase_chip',
      title: 'Chip',
      render: (_, record) => {
        return record?.allow_purchase_chip ? USAGE_LIMITS_STATES[record?.allow_purchase_chip] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_purchase_contactless',
      key: 'allow_purchase_contactless',
      title: 'Contactless',
      render: (_, record) => {
        return record?.allow_purchase_contactless ? USAGE_LIMITS_STATES[record?.allow_purchase_contactless] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_atm_operation',
      key: 'allow_atm_operation',
      title: 'Operaciones en cajeros',
      render: (_, record) => {
        return record?.allow_atm_operation ? USAGE_LIMITS_STATES[record?.allow_atm_operation] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_atm_withdrawal',
      key: 'allow_atm_withdrawal',
      title: 'Extracción por cajeros',
      render: (_, record) => {
        return record?.allow_atm_withdrawal ? USAGE_LIMITS_STATES[record?.allow_atm_withdrawal] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_oct',
      key: 'allow_oct',
      title: 'Operaciones OCT',
      render: (_, record) => {
        return record?.allow_oct ? USAGE_LIMITS_STATES[record?.allow_oct] : 'Indefinido'
      },
    },
    {
      dataIndex: 'allow_purchase_magnetic_stripe',
      key: 'allow_purchase_magnetic_stripe',
      title: 'Banda magnetica',
      render: (_, record) => {
        return record?.allow_purchase_magnetic_stripe ? USAGE_LIMITS_STATES[record?.allow_purchase_magnetic_stripe] : 'Indefinido'
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => handleViewUsageLimitDetailClick(e, record.id)}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Ver
          </Button>
        </Space>
      ),
    },
  ]

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  return (
    <PageWithFilters title="Limitadores de uso">
      <PageWithFiltersContentContainer>
        <div style={{ display: 'flex', marginBottom: '1.5rem', justifyContent: 'flex-end', gap: '1rem' }}>
          <NewUsageLimitBtn />
        </div>
        <Table
          dataSource={usageLimits?.data ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: usageLimits?.pagination?.total_rows,
            showTotal: (total) => usageLimits?.pagination?.total !== undefined ? `Total: ${total}` : ``,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
      <Modal
        open={ usageLimitModalOpen }
        onCancel={() => setUsageLimitModalOpen(false)}
        width={800}
        footer={null}
      >
        <NewUsageLimitForm execute={() => executeGet() } onCancel={() => setUsageLimitModalOpen(false)} />
      </Modal>
    </PageWithFilters>
  )
}

export default UsageLimitsPage;